import { useTranslation } from 'react-i18next'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { periodFlightCategory, periodInForce, type TafForecast, type TafPeriod } from '@jupi/shared'
import {
  cloudsWithOktas,
  describeWeather,
  FLIGHT_CATEGORY_COLORS,
  utcDayAndTime,
  visibilityText,
  windText,
} from './weatherFormat'

function periodLabel(period: TafPeriod, t: (key: string, options?: Record<string, unknown>) => string) {
  switch (period.change) {
    case null:
      return t('weather.taf.base')
    case 'FM':
      return t('weather.taf.from')
    case 'BECMG':
      return t('weather.taf.becoming')
    case 'TEMPO':
      return period.probability === null
        ? t('weather.taf.tempo')
        : t('weather.taf.probTempo', { probability: period.probability })
    case 'PROB':
      return t('weather.taf.prob', { probability: period.probability })
  }
}

export function TafDecoded({ taf, now }: { taf: TafForecast; now: number }) {
  const { t } = useTranslation()
  return (
    <Stack spacing={1}>
      {taf.periods.map((period) => (
        <PeriodBlock key={`${period.change}-${period.from}-${period.to}`} period={period} now={now} />
      ))}
      {taf.temperatures.length > 0 ? (
        <Typography variant="body2" color="text.secondary">
          {taf.temperatures
            .map((temp) =>
              t(temp.kind === 'max' ? 'weather.taf.maxTemperature' : 'weather.taf.minTemperature', {
                temperature: temp.temperatureC,
                time: utcDayAndTime(temp.at),
              }),
            )
            .join(' · ')}
        </Typography>
      ) : null}
    </Stack>
  )
}

function PeriodBlock({ period, now }: { period: TafPeriod; now: number }) {
  const { t } = useTranslation()
  const category = periodFlightCategory(period)
  const inForce = periodInForce(period, now)
  const calm = t('weather.calm')

  const lines: [string, string][] = []
  if (period.wind) {
    lines.push([t('weather.wind'), windText(period.wind.directionDeg, period.wind.speedKt, period.wind.gustKt, calm)!])
  }
  if (period.cavok) lines.push([t('weather.visibility'), 'CAVOK'])
  else if (period.visibilityM !== null) lines.push([t('weather.visibility'), visibilityText(period.visibilityM)!])
  if (period.weather.length > 0) {
    lines.push([t('weather.presentWeather'), period.weather.map((code) => `${describeWeather(code, t)} (${code})`).join(', ')])
  }
  if (period.noSignificantWeather) lines.push([t('weather.presentWeather'), t('weather.taf.noSignificantWeather')])
  const clouds = cloudsWithOktas(period.clouds)
  if (clouds) lines.push([t('weather.clouds'), clouds])
  if (period.verticalVisibilityFt !== null) {
    lines.push([t('weather.taf.verticalVisibility'), `${period.verticalVisibilityFt} ft`])
  }

  return (
    <Box
      sx={{
        borderLeft: 4,
        borderColor: category ? FLIGHT_CATEGORY_COLORS[category] : 'divider',
        bgcolor: inForce ? 'action.selected' : 'action.hover',
        borderRadius: 1,
        px: 1.5,
        py: 1,
      }}
    >
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap', rowGap: 0.5, mb: 0.5 }}>
        <Typography variant="subtitle2" component="span">
          {periodLabel(period, t)}
        </Typography>
        <Typography variant="body2" component="span" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
          {period.change === 'FM'
            ? `${utcDayAndTime(period.from)} UTC`
            : `${utcDayAndTime(period.from)}–${utcDayAndTime(period.to)} UTC`}
        </Typography>
        {category ? (
          <Tooltip title={t('weather.taf.categoryHint')}>
            <Chip
              size="small"
              label={category}
              sx={{ bgcolor: FLIGHT_CATEGORY_COLORS[category], color: '#fff', fontWeight: 700, height: 20 }}
            />
          </Tooltip>
        ) : null}
        {inForce ? <Chip size="small" color="primary" variant="outlined" label={t('weather.taf.now')} sx={{ height: 20 }} /> : null}
      </Stack>
      {lines.length > 0 ? (
        <Box
          component="dl"
          sx={{ display: 'grid', gridTemplateColumns: 'max-content 1fr', columnGap: 2, rowGap: 0.25, m: 0 }}
        >
          {lines.map(([label, value]) => (
            <Box key={`${label}-${value}`} sx={{ display: 'contents' }}>
              <Typography component="dt" variant="body2" color="text.secondary">
                {label}
              </Typography>
              <Typography component="dd" variant="body2" sx={{ m: 0, fontWeight: 500 }}>
                {value}
              </Typography>
            </Box>
          ))}
        </Box>
      ) : (
        <Typography variant="body2" color="text.secondary">
          {t('weather.taf.noChange')}
        </Typography>
      )}
    </Box>
  )
}
